/**
 * Weekly Deliverables — Brand-facing list page.
 *
 * Shows all deliveries for the brand (newest first). Each card surfaces the
 * title, due date, note, status, and a quick "X of Y approved" mosaic.
 * Clicking a card navigates to the per-delivery review board.
 *
 * User-scoped beta gated: `weekly_deliverables` is available only when the
 * requesting account has the grant. Read from /api/brands/:brandId/beta-features
 * (effective-flags API, fail-closed).
 */

import React, { useState } from "react";
import { useParams, useLocation, Redirect } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow, format, isPast } from "date-fns";
import {
  CalendarClock, ChevronRight, Clock, Inbox, Loader2, CheckCircle2,
  AlertTriangle, RotateCcw, Eye, Layers,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";
import { AdImage } from "@/components/ad-image";
import {
  summarizeDeliverable,
  deriveDeliverableStatus,
  deriveDeliveryStatus,
  type DeliverableStatus,
  type DeliveryStatus,
  WEEKLY_DELIVERABLES_FLAG_KEY,
} from "@shared/deliverables";

// ── Types ──────────────────────────────────────────────────────────────────

export type DeliveryItemAdDto = {
  itemAdId: string;
  adId: string | null;
  displayName: string;
  aspectRatio: string | null;
  imageUrl: string | null;
  sortOrder: number;
  removed: boolean;
};

export type DeliveryItemDecisionDto = {
  id: string;
  deliveryItemAdId: string;
  decision: "approved" | "rejected";
  reasons: string[];
  comment: string | null;
  disposition: "rework" | "drop" | null;
  positiveTags: string[];
  authorId: string | null;
  authorLabel: string | null;
  pinX: number | null;
  pinY: number | null;
  imageUrlSnapshot: string | null;
  createdAt: string | null;
};

export type DeliveryItemDto = {
  id: string;
  deliveryId: string;
  projectNameSnapshot: string;
  note: string | null;
  sortOrder: number;
  submittedAt: string | null;
  projectRejectedAt: string | null;
  projectRejectedBy: string | null;
  projectRejectedByLabel: string | null;
  projectRejectReasons: string[] | null;
  projectRejectComment: string | null;
  derivedStatus?: DeliverableStatus;
  summary?: {
    approved: number;
    rejected: number;
    unanswered: number;
    reworkCount: number;
    dropCount: number;
    label: string;
  };
  ads: DeliveryItemAdDto[];
  decisions: DeliveryItemDecisionDto[];
};

export type DeliveryDto = {
  id: string;
  brandId: string;
  title: string;
  note: string | null;
  status: "draft" | "delivered" | "closed" | "revoked";
  dueAt: string | null;
  publishedAt: string | null;
  closedAt: string | null;
  createdAt: string | null;
  derivedStatus?: DeliveryStatus;
  items: DeliveryItemDto[];
};

// ── Helpers ────────────────────────────────────────────────────────────────

function deliveryStatus(d: DeliveryDto): DeliveryStatus | "revoked" {
  if (d.status === "revoked") return "revoked";
  if (d.derivedStatus) return d.derivedStatus;
  return deriveDeliveryStatus({
    storedStatus: d.status,
    items: d.items.map((item) => ({
      adCount: item.ads.filter((a) => !a.removed).length,
      decisions: item.decisions.map((dec) => ({
        deliveryItemAdId: dec.deliveryItemAdId,
        decision: dec.decision,
        reasons: dec.reasons,
        comment: dec.comment,
        disposition: dec.disposition,
      })),
      submittedAt: item.submittedAt,
      projectRejectedAt: item.projectRejectedAt,
    })),
  });
}

function statusLabel(s: DeliveryStatus | "revoked"): string {
  switch (s) {
    case "draft": return "Draft";
    case "delivered": return "Ready for review";
    case "in_review": return "In review";
    case "complete": return "Complete";
    case "closed": return "Closed";
    case "revoked": return "Revoked";
  }
}

function statusBadgeClass(s: DeliveryStatus | "revoked"): string {
  switch (s) {
    case "draft": return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
    case "delivered": return "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300";
    case "in_review": return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
    case "complete": return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300";
    case "closed": return "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400";
    case "revoked": return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
  }
}

function StatusIcon({ s }: { s: DeliveryStatus | "revoked" }) {
  switch (s) {
    case "complete": return <CheckCircle2 className="h-3.5 w-3.5" />;
    case "delivered": return <Clock className="h-3.5 w-3.5" />;
    case "in_review": return <Eye className="h-3.5 w-3.5" />;
    case "closed": return <RotateCcw className="h-3.5 w-3.5" />;
    case "revoked": return <AlertTriangle className="h-3.5 w-3.5" />;
    default: return <Layers className="h-3.5 w-3.5" />;
  }
}

/** Count unreviewed deliveries (delivered/in_review status = needs action, revoked excluded). */
export function unreviewedCount(deliveries: DeliveryDto[]): number {
  return deliveries.filter((d) => {
    const s = deliveryStatus(d);
    return s === "delivered" || s === "in_review";
  }).length;
}

// ── Flag guard hook ────────────────────────────────────────────────────────

export function useWeeklyDeliverablesEnabled(brandId: string | undefined) {
  const { user } = useAuth();
  const { data, isPending, isError } = useQuery<Record<string, boolean>>({
    queryKey: ["/api/brands", brandId, "beta-features"],
    enabled: !!brandId && !!user,
    retry: false,
    staleTime: 30_000,
  });
  return {
    enabled: data?.[WEEKLY_DELIVERABLES_FLAG_KEY] === true,
    isUnknown: isPending && !isError,
    isError,
  };
}

// ── Image mosaic for a delivery card ──────────────────────────────────────

function DeliveryMosaic({ items }: { items: DeliveryItemDto[] }) {
  const allAds = items.flatMap((item) => item.ads.filter((a) => !a.removed && a.imageUrl));
  const show = allAds.slice(0, 4);
  if (show.length === 0) return null;
  return (
    <div
      className={`grid gap-1 rounded-xl overflow-hidden ${
        show.length === 1
          ? "grid-cols-1"
          : show.length === 2
          ? "grid-cols-2"
          : show.length === 3
          ? "grid-cols-3"
          : "grid-cols-2"
      }`}
      aria-hidden="true"
    >
      {show.map((ad) => (
        <div
          key={ad.itemAdId}
          className="aspect-square bg-muted/30 overflow-hidden"
        >
          <AdImage
            src={ad.imageUrl}
            alt={ad.displayName}
            thumbWidth={160}
            className="w-full h-full object-cover"
          />
        </div>
      ))}
    </div>
  );
}

// ── Delivery card ──────────────────────────────────────────────────────────

function DeliveryCard({
  delivery,
  onClick,
}: {
  delivery: DeliveryDto;
  onClick: () => void;
}) {
  const derived = deliveryStatus(delivery);
  const totalAds = delivery.items.reduce(
    (acc, item) => acc + item.ads.filter((a) => !a.removed).length,
    0,
  );
  const hasServerSummary = delivery.items.every((item) => !!item.summary);
  const summary = hasServerSummary
    ? delivery.items.reduce(
        (acc, item) => ({
          approved: acc.approved + (item.summary?.approved ?? 0),
          rejected: acc.rejected + (item.summary?.rejected ?? 0),
          unanswered: acc.unanswered + (item.summary?.unanswered ?? 0),
          reworkCount: acc.reworkCount + (item.summary?.reworkCount ?? 0),
          dropCount: acc.dropCount + (item.summary?.dropCount ?? 0),
          label: "",
        }),
        { approved: 0, rejected: 0, unanswered: 0, reworkCount: 0, dropCount: 0, label: "" },
      )
    : summarizeDeliverable({
        adCount: totalAds,
        decisions: delivery.items.flatMap((item) =>
          item.decisions.map((dec) => ({
            deliveryItemAdId: dec.deliveryItemAdId,
            decision: dec.decision,
            reasons: dec.reasons,
            comment: dec.comment,
            disposition: dec.disposition,
          })),
        ),
      });
  const isDue = delivery.dueAt && !isPast(new Date(delivery.dueAt));
  const isOverdue = delivery.dueAt && isPast(new Date(delivery.dueAt)) && derived !== "complete" && derived !== "closed";

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className="deliverables-card group cursor-pointer overflow-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
      data-testid={`card-delivery-${delivery.id}`}
    >
      {/* Mosaic strip */}
      {delivery.items.length > 0 && (
        <div className="bg-muted/20 p-3 pb-0">
          <DeliveryMosaic items={delivery.items} />
        </div>
      )}

      <div className="p-4 space-y-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3
              className="text-[15px] font-semibold leading-snug truncate group-hover:text-primary transition-colors"
              data-testid={`text-delivery-title-${delivery.id}`}
            >
              {delivery.title}
            </h3>
            {delivery.note && (
              <p
                className="text-xs text-muted-foreground mt-0.5 line-clamp-2"
                data-testid={`text-delivery-note-${delivery.id}`}
              >
                {delivery.note}
              </p>
            )}
          </div>
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${statusBadgeClass(derived)}`}
            data-testid={`badge-delivery-status-${delivery.id}`}
          >
            <StatusIcon s={derived} />
            {statusLabel(derived)}
          </span>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span data-testid={`text-delivery-summary-${delivery.id}`}>
            {totalAds > 0 ? `${summary.approved} of ${totalAds} approved` : `${delivery.items.length} project${delivery.items.length !== 1 ? "s" : ""}`}
          </span>
          {delivery.dueAt && (
            <span
              className={`flex items-center gap-1 ${isOverdue ? "text-destructive font-medium" : ""}`}
              data-testid={`text-delivery-due-${delivery.id}`}
            >
              <CalendarClock className="h-3 w-3" />
              {isOverdue
                ? `Overdue · ${format(new Date(delivery.dueAt), "MMM d")}`
                : isDue
                ? `Due ${formatDistanceToNow(new Date(delivery.dueAt), { addSuffix: true })}`
                : format(new Date(delivery.dueAt), "MMM d, yyyy")}
            </span>
          )}
          {delivery.createdAt && (
            <span className="text-muted-foreground/60">
              {format(new Date(delivery.createdAt), "MMM d, yyyy")}
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1 flex-wrap">
            {summary.unanswered > 0 && derived !== "complete" && derived !== "closed" && (
              <span className="text-[11px] font-medium text-amber-600 dark:text-amber-500">
                {summary.unanswered} awaiting review
              </span>
            )}
            {summary.rejected > 0 && (
              <span className="text-[11px] text-destructive font-medium">
                {summary.rejected} rejected
              </span>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
      </div>
    </Card>
  );
}

// ── Guard: redirects when flag is off ─────────────────────────────────────

export function WeeklyDeliverablesGuard({ children }: { children: React.ReactNode }) {
  const { brandId } = useParams<{ brandId?: string }>();
  const { enabled, isUnknown } = useWeeklyDeliverablesEnabled(brandId);
  if (isUnknown) return null;
  if (!enabled) return <Redirect to={brandId ? `/brands/${brandId}` : "/"} />;
  return <>{children}</>;
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function DeliverablesPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { enabled, isUnknown } = useWeeklyDeliverablesEnabled(brandId);

  const { data: deliveries, isLoading, isError, error } = useQuery<DeliveryDto[]>({
    queryKey: ["/api/brands", brandId, "deliveries"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/deliveries`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    enabled: !!brandId && !!user && enabled,
    staleTime: 3_000,
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
  });

  // Fail-closed guard
  if (authLoading || isUnknown) return null;
  if (!enabled) return <Redirect to={brandId ? `/brands/${brandId}` : "/"} />;

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-56" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-56 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[40vh] gap-4 p-6"
        data-testid="state-deliverables-error"
      >
        <AlertTriangle className="h-10 w-10 text-destructive/70" />
        <p className="text-sm text-muted-foreground max-w-sm text-center">
          {(error as Error)?.message || "Could not load deliveries. Please try again."}
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  const list = deliveries ?? [];

  if (list.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-6 text-center"
        data-testid="state-deliverables-empty"
      >
        <div className="rounded-full bg-primary/10 p-4">
          <Inbox className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-lg font-semibold">No deliveries yet</h2>
        <p className="text-sm text-muted-foreground max-w-xs">
          Your team will deliver ads here for your review. You'll get notified when something is ready.
        </p>
      </div>
    );
  }

  // Active deliveries remain visible until an internal user explicitly closes
  // them. A fully submitted delivery derives as complete but is still live.
  const activeDeliveries = [...list]
    .filter((d) => {
      const s = deliveryStatus(d);
      return s === "delivered" || s === "in_review" || s === "complete";
    })
    .sort((a, b) => (b.publishedAt ?? b.createdAt ?? "").localeCompare(a.publishedAt ?? a.createdAt ?? ""));
  // History: closed deliveries
  const history = [...list]
    .filter((d) => d.status === "closed")
    .sort((a, b) => (b.closedAt ?? b.createdAt ?? "").localeCompare(a.closedAt ?? a.createdAt ?? ""));
  // Revoked deliveries (internal history, shown separately)
  const revoked = [...list]
    .filter((d) => d.status === "revoked")
    .sort((a, b) => (b.publishedAt ?? b.createdAt ?? "").localeCompare(a.publishedAt ?? a.createdAt ?? ""));

  const unreviewed = unreviewedCount(list);

  return (
    <div className="deliverables-surface min-h-[calc(100dvh-1px)] p-4 sm:p-8 space-y-7">
      <div className="deliverables-hero rounded-2xl px-5 py-6 sm:px-8 sm:py-7 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/75 mb-2">Brand review workspace</p>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight"
            data-testid="heading-deliverables"
          >
            Deliverables
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {unreviewed} {unreviewed === 1 ? "delivery" : "deliveries"} waiting for you
        </div>
      </div>

      <section className="space-y-3" data-testid="section-current-delivery">
        <div>
          <h2 className="text-lg font-semibold">Ready for review</h2>
          <p className="text-xs text-muted-foreground">The latest work ready for your feedback.</p>
        </div>
        {activeDeliveries.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="grid-deliveries">
            {activeDeliveries.map((delivery) => (
              <DeliveryCard
                key={delivery.id}
                delivery={delivery}
                onClick={() => setLocation(`/brands/${brandId}/deliverables/${delivery.id}`)}
              />
            ))}
          </div>
        ) : (
          <Card className="deliverables-card px-5 py-8 text-center">
            <p className="text-sm font-medium">Nothing to review right now.</p>
            <p className="text-xs text-muted-foreground mt-1">
              You&apos;ll be notified when new work is ready for review.
            </p>
          </Card>
        )}
      </section>

      {history.length > 0 && (
        <details className="group rounded-2xl border border-border/60 bg-card/60 p-4" data-testid="section-delivery-history">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">History</h2>
              <p className="text-xs text-muted-foreground">{history.length} closed deliver{history.length === 1 ? "y" : "ies"}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 mt-4">
            {history.map((delivery) => (
              <DeliveryCard
                key={delivery.id}
                delivery={delivery}
                onClick={() => setLocation(`/brands/${brandId}/deliverables/${delivery.id}`)}
              />
            ))}
          </div>
        </details>
      )}

      {revoked.length > 0 && (
        <details className="group rounded-2xl border border-border/60 bg-card/60 p-4" data-testid="section-delivery-revoked">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">Revoked</h2>
              <p className="text-xs text-muted-foreground">{revoked.length} revoked deliver{revoked.length === 1 ? "y" : "ies"} — internal history</p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 mt-4">
            {revoked.map((delivery) => (
              <DeliveryCard
                key={delivery.id}
                delivery={delivery}
                onClick={() => setLocation(`/brands/${brandId}/deliverables/${delivery.id}`)}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
